import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  DeliveryBookBody,
  DeliveryLastPrice,
  DeliveryWriteBody,
} from '@/api/endpoints/inventory-deliveries'
import { inventoryDeliveriesApi } from '@/api/endpoints/inventory-deliveries'

export const deliveryListKey = (restaurantId: number) => [
  'inventory-deliveries',
  restaurantId,
]

export const deliveryDetailKey = (restaurantId: number, id: string) => [
  'inventory-delivery',
  restaurantId,
  id,
]

export function useDelivery(restaurantId: number | null, id: string | null) {
  return useQuery({
    queryKey: deliveryDetailKey(restaurantId ?? 0, id ?? ''),
    queryFn: () =>
      inventoryDeliveriesApi.get(restaurantId as number, id as string),
    enabled: restaurantId != null && id != null,
    retry: false,
  })
}

/**
 * "Letzter EK" per SKU at the store (task 9.5): one request for the whole set, keyed by the sorted
 * SKUs so adding a position refetches while re-ordering does not. Empty until loaded.
 */
export function useLastPrices(
  restaurantId: number | null,
  skus: Array<string>,
): Map<string, DeliveryLastPrice> {
  const sorted = [...new Set(skus)].sort()
  const query = useQuery({
    queryKey: ['inventory-delivery-last-prices', restaurantId ?? 0, sorted],
    queryFn: () =>
      inventoryDeliveriesApi.lastPrices(restaurantId as number, sorted),
    enabled: restaurantId != null && sorted.length > 0,
    staleTime: 60_000,
  })
  return useMemo(
    () => new Map((query.data?.items ?? []).map((p) => [p.sku, p])),
    [query.data],
  )
}

/**
 * Every write moves stock or the projection of it, so the list, the document, the inventory page
 * (its `Eingehend` column and sellable figure) and the history all go stale at once.
 */
function useInvalidateDeliveries(restaurantId: number | null) {
  const queryClient = useQueryClient()
  return (id?: string) => {
    // The list is rendered by a DataTable whose cache key is a single string carrying the
    // restaurant and the status filter, so it is matched by prefix rather than by key equality.
    queryClient.invalidateQueries({
      predicate: (q) => {
        const head = q.queryKey[0]
        return (
          typeof head === 'string' &&
          (head.startsWith('inventory-deliveries') ||
            // A booking can set a newer "Letzter EK".
            head.startsWith('inventory-delivery-last-prices') ||
            head.startsWith('inventory-page') ||
            head.startsWith('inventory-history'))
        )
      },
    })
    if (id) {
      queryClient.invalidateQueries({
        queryKey: deliveryDetailKey(restaurantId ?? 0, id),
      })
    }
    queryClient.invalidateQueries({ queryKey: ['inventory'] })
    queryClient.invalidateQueries({ queryKey: ['inventory-history'] })
  }
}

export function useCreateDelivery(restaurantId: number | null) {
  const invalidate = useInvalidateDeliveries(restaurantId)
  return useMutation({
    mutationFn: (body: DeliveryWriteBody) =>
      inventoryDeliveriesApi.create(restaurantId as number, body),
    onSuccess: (result) => invalidate(result.delivery.id),
  })
}

export function useUpdateDelivery(
  restaurantId: number | null,
  id: string | null,
) {
  const invalidate = useInvalidateDeliveries(restaurantId)
  return useMutation({
    mutationFn: (body: DeliveryWriteBody) =>
      inventoryDeliveriesApi.update(restaurantId as number, id as string, body),
    onSuccess: () => invalidate(id ?? undefined),
  })
}

export function useBookDelivery(
  restaurantId: number | null,
  id: string | null,
) {
  const invalidate = useInvalidateDeliveries(restaurantId)
  return useMutation({
    mutationFn: (body: DeliveryBookBody) =>
      inventoryDeliveriesApi.book(restaurantId as number, id as string, body),
    onSuccess: () => invalidate(id ?? undefined),
  })
}

export function useCancelDelivery(
  restaurantId: number | null,
  id: string | null,
) {
  const invalidate = useInvalidateDeliveries(restaurantId)
  return useMutation({
    mutationFn: () =>
      inventoryDeliveriesApi.cancel(restaurantId as number, id as string),
    onSuccess: () => invalidate(id ?? undefined),
  })
}
